import { useId, useState } from 'react'
import { Props_SnippetContentColumn } from './ContentColumn.props'
import { Legacy_CompileMarkdown } from '../../../../Shared/AttraxTs/React/ReactSnippetMDCompiler'
import { convertIframeUrl } from '../Video/Video'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation, Pagination, A11y } from 'swiper/modules'
import SwiperControls, { swiperNavigationOptions } from '../Shared/SwiperControls/SwiperControls'

const classBase: string = 'snippet-content-column'

type SnippetContentColumnEntryItemProps = {
    entry: any
    index: number
    id: string
}

const SnippetContentColumnEntry: React.FC<SnippetContentColumnEntryItemProps> = ({ entry, index, id }) => {
    const [playing, setPlaying] = useState(false)
    const classVideoPlaying: string = playing ? `${classBase}__entry-media-container--playing` : ''

    return (
        <div key={`snippet-content-column__entry-${id}-${index}`} className="snippet-content-column__entry">
        { entry.image?.value && (
            <figure className={`snippet-content-column__entry-media-container ${classVideoPlaying}`}>
                <img
                    className={`snippet-content-column__entry-image snippet-content-column__entry-image--fit-${entry.objectFit?.value}`}
                    loading="lazy"
                    height={190}
                    width={entry.video?.value ? 400 : 350}
                    src={entry.image.value}
                    alt={entry.alt?.value ? entry.alt.value : ''}
                />
                { entry.video?.value && (
                    <>
                        <button className="snippet-content-column__entry-video-button play-button" aria-label={`{{js.snippet.content-column.video.play-button.aria-label}}`} onClick={() => setPlaying(true)}>
                            <span className="play-button__icon"></span>
                            <span className="play-button__text">{`{{js.snippet.content-column.video.play-button.label}}`}</span>
                        </button>
                        <iframe
                            width="560"
                            height="315"
                            src={playing && entry.video?.value ? convertIframeUrl(entry.video.value) : ''}
                            title="YouTube video player"
                            loading="lazy"
                            allow="accelerometer autoplay clipboard-write encrypted-media gyroscope picture-in-picture web-share"
                            referrerPolicy="strict-origin-when-cross-origin"
                            className="snippet-content-column__entry-video"
                            allowFullScreen={true}
                        ></iframe>
                    </>
                )}
            </figure>
        )}
        <div className="snippet-content-column__entry-content-container">
            { entry.heading?.value && <h3 className={`snippet-content-column__entry-title snippet-content-column__entry-title--alignment-${entry.headingAlignment?.value}`}>{ entry.heading.value }</h3>}
            { entry.content?.value && <div className="snippet-content-column__entry-content">{ Legacy_CompileMarkdown(entry.content.value) }</div> }
            { entry.linkText?.value && entry.url?.value && entry.openInNewTab?.value && (
                <a
                    href={entry.url.value}
                    className="snippet-content-column__entry-link"
                    target={entry.openInNewTab.value === 'true' ? '_blank' : '_self'}
                    rel={entry.openInNewTab.value === 'true' ? 'noopener noreferrer' : ''}
                >
                    { entry.linkText.value }
                </a>
            )}
        </div>
    </div>
    )
}

export const SnippetContentColumn = (props: Props_SnippetContentColumn) => {
    const id = useId()
    const { snippet, heading, repeatable_entry } = props

    const uniqueId = useId().replace(/[^a-zA-Z0-9-_]/g, '')
    const carouselId = `snippet-content-column-carousel-${uniqueId}`

    const enableCarousel: boolean = snippet.carousel?.value === 'true' ? true : false
    const classSnippetMediaType: string = snippet.mediaType.value ? `${classBase}--${snippet.mediaType.value}` : ''
    const classSnippetTheme: string = snippet.theme.value ? `${classBase}--theme-${snippet.theme.value}` : ''
    const classSnippetCardType: string = snippet.cardType.value ? `${classBase}--card-type-${snippet.cardType.value}` : ''
    const classHeadingVisibility: string = heading.visibility.value ? `${classBase}__heading--${heading.visibility.value}` : ''

    return (
    <section className={`${classBase} ${classSnippetMediaType} ${classSnippetTheme} ${classSnippetCardType}`}>
        <div className={`snippet-content-column__container`}>
            <h2 className={`snippet-content-column__heading ${classHeadingVisibility}`}>{ heading.content.value }</h2>
            <div className="snippet-content-column__container-entries">
                { enableCarousel && (
                    <>
                    <Swiper
                        modules={[Navigation, Pagination, A11y]}
                        spaceBetween={20}
                        slidesPerView={'auto'}
                        freeMode={true}
                        watchOverflow={true}
                        watchSlidesProgress={true}
                        navigation={{
                            ...swiperNavigationOptions,
                            prevEl: `.swiper-navigation__button-prev-${carouselId}`,
                            nextEl: `.swiper-navigation__button-next-${carouselId}`,
                        }}
                        breakpoints={{
                            991: {
                                spaceBetween: 40,
                            }
                        }}
                        className="snippet-content-column__swiper"
                        >
                        { repeatable_entry.map((entry, index: number) => (
                            <SwiperSlide key={`snippet-content-column__entry-${id}-${index}`}>
                                <SnippetContentColumnEntry entry={entry} index={index} id={id} />
                            </SwiperSlide>
                        )) }
                    </Swiper>
                    <SwiperControls 
                        id={carouselId}
                        showNavigationPrev={true}
                        showNavigationNext={true}
                        showPagination={false}
                        className="snippet-content-column-carousel__swiper-controls"
                        />
                    </>
                )}
                { !enableCarousel && (
                    repeatable_entry.map((entry, index: number) => (
                        <SnippetContentColumnEntry entry={entry} index={index} id={id} key={`snippet-content-column__entry-${id}-${index}`} />
                    ))
                )}
                </div>
            </div>
        </section>
    )
}

export default SnippetContentColumn